import { Link } from "@tanstack/react-router";
import { Card, CardBody, CardHeader, Skeleton, StatusPill } from "@flint/ui";
import { StoreAdminLayout } from "../store-admin-layout.tsx";
import { BackButton } from "../../workspace/index.ts";
import { StorePageColumn } from "../store-page-column.tsx";
import { trpc } from "../../../lib/trpc.ts";
import {
  ORDER_STATUS_LABEL,
  ORDER_STATUS_TONE,
  exactMoney,
  orderMoney,
  shortDate,
} from "../orders/order-format.ts";
import { QUOTE_PHASE_LABEL, QUOTE_PHASE_TONE } from "../quotes/quote-format.ts";
import { PrebookPill } from "../prebook-pill.tsx";

/**
 * One figure in the header strip. Kept local — four tiles on one screen is not
 * yet a shared primitive, and the collection page's rail is a different shape.
 */
function Stat({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="rounded border border-line bg-paper px-4 py-3 shadow-sm">
      <p className="text-xs uppercase tracking-wide text-muted">{label}</p>
      <p className="mt-1 font-mono text-xl font-semibold tabular-nums text-ink">{value}</p>
      {hint ? <p className="mt-0.5 text-xs text-muted">{hint}</p> : null}
    </div>
  );
}

/**
 * Store admin — Buyer detail (Commerce, M3).
 *
 * The account, four figures, and the two ledgers a seller asks about: what this
 * buyer ordered and what they were quoted. Read-only, like the directory — the
 * actions live on the order and quote pages this links out to.
 *
 * **Why the orders table can be longer than the "Orders" tile.** The tile counts
 * commitments (confirmed and beyond), matching the directory and the collection
 * performance panel; the table is the full history including drafts and
 * rejections, because the order that fell through is exactly what a seller
 * opens this page to find. The tile says "committed" so the two never read as a
 * contradiction.
 *
 * @see packages/domain/src/commerce/store/get-buyer.ts
 */
export function BuyerDetailPage({ buyerId }: { buyerId: string }) {
  const q = trpc.storeBuyers.get.useQuery({ buyerId });
  const b = q.data;

  if (q.isLoading) {
    return (
      <StoreAdminLayout breadcrumbs={[{ label: "Store", to: "/store/admin" }, { label: "Buyers" }]}>
        <StorePageColumn variant="grid">
          <Skeleton className="h-8 w-64" />
          <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-20 w-full" />
            ))}
          </div>
          <Skeleton className="mt-4 h-64 w-full" />
        </StorePageColumn>
      </StoreAdminLayout>
    );
  }

  if (!b) {
    return (
      <StoreAdminLayout breadcrumbs={[{ label: "Store", to: "/store/admin" }, { label: "Buyers" }]}>
        <StorePageColumn variant="grid">
          <BackButton fallbackTo="/store/admin/buyers" label="Back to buyers" />
          <p className="mt-6 text-sm text-muted">
            That buyer doesn&rsquo;t exist, or belongs to another store.
          </p>
        </StorePageColumn>
      </StoreAdminLayout>
    );
  }

  return (
    <StoreAdminLayout
      breadcrumbs={[
        { label: "Store", to: "/store/admin" },
        { label: "Buyers", to: "/store/admin/buyers" },
        { label: b.businessName },
      ]}
    >
      <StorePageColumn variant="grid">
        <BackButton fallbackTo="/store/admin/buyers" label="Back to buyers" />

        <div className="mt-3 flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-2xl font-bold tracking-tight text-ink leading-tight">
                {b.businessName}
              </h1>
              <StatusPill tone={b.status === "active" ? "ok" : "bad"} size="sm" dot>
                {b.status === "active" ? "Active" : "Suspended"}
              </StatusPill>
            </div>
            <p className="mt-1.5 text-sm text-muted">
              {b.name} · <span className="font-mono tabular-nums">{b.phone}</span> ·{" "}
              {/* A buyer without a GSTIN is invoiced B2C — worth stating, not
                  hiding behind a blank. */}
              {b.gstin ? (
                <span className="font-mono">{b.gstin}</span>
              ) : (
                <span>No GSTIN — invoiced B2C</span>
              )}{" "}
              · Joined {shortDate(b.joinedAt)}
            </p>
          </div>
        </div>

        <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Stat label="Committed orders" value={String(b.orderCount)} hint="Confirmed and beyond" />
          <Stat label="Lifetime value" value={orderMoney(b.orderValuePaise)} hint="Incl. GST" />
          <Stat
            label="Average order"
            value={b.avgOrderValuePaise === null ? "—" : orderMoney(b.avgOrderValuePaise)}
          />
          <Stat label="Open quotes" value={String(b.openQuoteCount)} hint="Awaiting this buyer" />
        </div>

        <Card className="mt-4">
          <CardHeader>
            <h2 className="text-sm font-semibold text-ink">Orders</h2>
          </CardHeader>
          <CardBody className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[720px] text-left text-sm">
                <thead className="border-b border-line bg-raised text-xs uppercase tracking-wide text-muted">
                  <tr>
                    <th className="px-3 py-3 text-left font-medium">#</th>
                    <th className="px-3 py-3 text-left font-medium">Date</th>
                    <th className="px-3 py-3 text-right font-medium">Qty</th>
                    <th className="px-3 py-3 text-right font-medium">Value</th>
                    <th className="px-3 py-3 text-left font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {b.orders.map((o) => (
                    <tr key={o.orderId} className="border-b border-line last:border-0 hover:bg-raised">
                      <td className="px-3 py-3">
                        <span className="flex items-center gap-2">
                          <Link
                            to="/store/admin/orders/$orderId"
                            params={{ orderId: o.orderId }}
                            className="font-mono text-sm font-medium text-ink hover:text-sky-700"
                          >
                            {o.docNumber || "Unnumbered"}
                          </Link>
                          <PrebookPill kind={o.kind} />
                        </span>
                      </td>
                      <td className="px-3 py-3 font-mono tabular-nums text-ink-2">
                        {shortDate(o.createdAt)}
                      </td>
                      <td className="px-3 py-3 text-right font-mono tabular-nums text-ink">
                        {o.qty}
                      </td>
                      <td className="px-3 py-3 text-right font-mono tabular-nums text-ink">
                        {exactMoney(o.totalPaise)}
                      </td>
                      <td className="px-3 py-3">
                        <StatusPill tone={ORDER_STATUS_TONE[o.status]} size="sm" dot>
                          {ORDER_STATUS_LABEL[o.status]}
                        </StatusPill>
                      </td>
                    </tr>
                  ))}
                  {b.orders.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-3 py-10 text-center text-sm text-muted">
                        No orders yet.
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          </CardBody>
        </Card>

        <Card className="mt-4">
          <CardHeader>
            <h2 className="text-sm font-semibold text-ink">Quotes</h2>
          </CardHeader>
          <CardBody className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[720px] text-left text-sm">
                <thead className="border-b border-line bg-raised text-xs uppercase tracking-wide text-muted">
                  <tr>
                    <th className="px-3 py-3 text-left font-medium">#</th>
                    <th className="px-3 py-3 text-left font-medium">Raised</th>
                    <th className="px-3 py-3 text-left font-medium">Expires</th>
                    <th className="px-3 py-3 text-right font-medium">Value</th>
                    <th className="px-3 py-3 text-left font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {b.quotes.map((qt) => (
                    <tr key={qt.quoteId} className="border-b border-line last:border-0 hover:bg-raised">
                      <td className="px-3 py-3">
                        <Link
                          to="/store/admin/quotes/$quoteId"
                          params={{ quoteId: qt.quoteId }}
                          className="font-mono text-sm font-medium text-ink hover:text-sky-700"
                        >
                          {qt.docNumber || "Unnumbered"}
                        </Link>
                      </td>
                      <td className="px-3 py-3 font-mono tabular-nums text-ink-2">
                        {shortDate(qt.createdAt)}
                      </td>
                      <td className="px-3 py-3 font-mono tabular-nums text-ink-2">
                        {qt.expiresOn ?? "—"}
                      </td>
                      <td className="px-3 py-3 text-right font-mono tabular-nums text-ink">
                        {exactMoney(qt.totalPaise)}
                      </td>
                      <td className="px-3 py-3">
                        <StatusPill tone={QUOTE_PHASE_TONE[qt.phase]} size="sm" dot>
                          {QUOTE_PHASE_LABEL[qt.phase]}
                        </StatusPill>
                      </td>
                    </tr>
                  ))}
                  {b.quotes.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-3 py-10 text-center text-sm text-muted">
                        No quotes raised for this buyer.
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          </CardBody>
        </Card>
      </StorePageColumn>
    </StoreAdminLayout>
  );
}
